import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { CheckCircle, Close, ArrowBack } from '@mui/icons-material';

const PricingPage = () => {
    const navigate = useNavigate();

    const plans = [
        {
            name: 'Free',
            price: '0',
            color: '#757575',
            features: {
                messages: '100 رسالة/شهر',
                accounts: '1 حساب لكل منصة',
                campaigns: '2 حملة',
                chatbot: true,
                analytics: 'أساسية',
                support: 'دعم المجتمع',
                api: false,
                customIntegration: false,
                dedicatedManager: false,
            },
        },
        {
            name: 'Pro',
            price: '29',
            color: '#1976d2',
            recommended: true,
            features: {
                messages: '5,000 رسالة/شهر',
                accounts: '5 حسابات لكل منصة',
                campaigns: '20 حملة',
                chatbot: true,
                analytics: 'متقدمة',
                support: 'دعم ذو أولوية',
                api: true,
                customIntegration: false,
                dedicatedManager: false,
            },
        },
        {
            name: 'Enterprise',
            price: '99',
            color: '#9c27b0',
            features: {
                messages: 'غير محدود',
                accounts: 'غير محدود',
                campaigns: 'غير محدود',
                chatbot: true,
                analytics: 'شاملة + تقارير مخصصة',
                support: 'دعم 24/7',
                api: true,
                customIntegration: true,
                dedicatedManager: true,
            },
        },
    ];

    const featureComparison = [
        { name: 'عدد الرسائل الشهرية', key: 'messages' },
        { name: 'عدد الحسابات', key: 'accounts' },
        { name: 'الحملات التسويقية', key: 'campaigns' },
        { name: 'الردود التلقائية (Chatbot)', key: 'chatbot' },
        { name: 'التحليلات والإحصائيات', key: 'analytics' },
        { name: 'الدعم الفني', key: 'support' },
        { name: 'API للمطورين', key: 'api' },
        { name: 'تكامل مخصص', key: 'customIntegration' },
        { name: 'مدير حساب مخصص', key: 'dedicatedManager' },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, md: 4 } }}>
            <Container maxWidth="lg">
                {/* Header */}
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/')}
                    sx={{ mb: { xs: 2, md: 4 } }}
                >
                    العودة للرئيسية
                </Button>

                <Typography variant="h3" align="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>
                    اختر الخطة المناسبة لك
                </Typography>
                <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: { xs: 4, md: 6 } }}>
                    جميع الخطط تشمل 14 يوم تجربة مجانية
                </Typography>

                {/* Pricing Cards */}
                <Grid container spacing={4} sx={{ mb: 8 }}>
                    {plans.map((plan, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    position: 'relative',
                                    border: plan.recommended ? `3px solid ${plan.color}` : 'none',
                                    transform: plan.recommended ? 'scale(1.05)' : 'none',
                                }}
                            >
                                {plan.recommended && (
                                    <Chip
                                        label="الأكثر شعبية"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            bgcolor: plan.color,
                                            color: 'white',
                                            fontWeight: 'bold',
                                        }}
                                    />
                                )}
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {plan.name}
                                    </Typography>
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h3" fontWeight="bold" component="span">
                                            ${plan.price}
                                        </Typography>
                                        <Typography variant="body1" component="span" color="text.secondary">
                                            {' / شهرياً'}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant={plan.recommended ? 'contained' : 'outlined'}
                                        fullWidth
                                        size="large"
                                        sx={{
                                            bgcolor: plan.recommended ? plan.color : 'transparent',
                                            borderColor: plan.color,
                                            color: plan.recommended ? 'white' : plan.color,
                                            '&:hover': {
                                                bgcolor: plan.recommended ? plan.color : 'rgba(0,0,0,0.05)',
                                            },
                                        }}
                                        onClick={() => navigate('/register')}
                                    >
                                        ابدأ الآن
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Feature Comparison Table */}
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    مقارنة تفصيلية للمميزات
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الميزة</TableCell>
                                {plans.map((plan, index) => (
                                    <TableCell key={index} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                                        {plan.name}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {featureComparison.map((feature, index) => (
                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                        {feature.name}
                                    </TableCell>
                                    {plans.map((plan, planIndex) => (
                                        <TableCell key={planIndex} align="center">
                                            {typeof plan.features[feature.key] === 'boolean' ? (
                                                plan.features[feature.key] ? (
                                                    <CheckCircle sx={{ color: 'success.main' }} />
                                                ) : (
                                                    <Close sx={{ color: 'error.main' }} />
                                                )
                                            ) : (
                                                <Typography variant="body2">{plan.features[feature.key]}</Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* FAQ Section */}
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                    الأسئلة الشائعة
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    هل يمكنني تغيير خطتي لاحقاً؟
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    نعم، يمكنك الترقية أو التخفيض في أي وقت. سيتم احتساب الفرق بشكل تناسبي.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    ما هي طرق الدفع المتاحة؟
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    نقبل جميع بطاقات الائتمان الرئيسية عبر Stripe الآمن.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    هل هناك رسوم إضافية؟
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    لا، جميع المميزات المذكورة مشمولة في السعر بدون رسوم خفية.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    هل يمكنني إلغاء الاشتراك؟
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    نعم، يمكنك الإلغاء في أي وقت بدون أي التزامات طويلة الأمد.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* CTA */}
                <Box
                    sx={{
                        mt: 8,
                        p: { xs: 3, md: 6 },
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                        textAlign: 'center',
                        color: 'white',
                    }}
                >
                    <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                        جاهز للبدء؟
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, opacity: 0.9, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        ابدأ تجربتك المجانية لمدة 14 يوم الآن
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/register')}
                        sx={{
                            bgcolor: 'white',
                            color: '#667eea',
                            '&:hover': { bgcolor: '#f5f5f5' },
                            px: 5,
                            py: 1.5,
                        }}
                    >
                        ابدأ مجاناً
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default PricingPage;
